import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';
import Axios from 'axios';
import cookieCutter from 'cookie-cutter';
import Head from 'next/head';
import Image from 'next/image';
import getMetaTagData from '../../src/common/services/getMetaTagData';
import Slider from '../../src/component/pc/carousel/Slider';
import Loading from '../../src/component/pc/common/Loading';
import Title from '../../src/component/pc/common/Title';
import Info from '../../src/component/pc/live/InfoWrapper';
import Player from '../../src/component/pc/live/PlayerWrapper';
import Side from '../../src/component/pc/live/SideWrapper';

import { deduplArray, toTimeStamp } from '../../src/common/utils/convertUtil';
import {
  getLiveChannelType,
  isVirtualChannel,
  getLivePlanArray,
  toPlanUrl,
} from '../../src/common/utils/liveUtil';

import * as MODE from '../../src/common/constants/live/mode';

const LiveViewPage = (props) => {
  const router = useRouter();
  const [token, setToken] = useState(null);
  const [onair, setOnair] = useState(props.data?.onair || null);
  const [likeCnt, setLikeCnt] = useState(props.like_cnt);
  const [isClickLike, setIsClickLike] = useState(false);
  const [channelType, setChannelType] = useState('tv');
  const [mode, setMode] = useState(null);
  const [focusname, setFocusName] = useState(null);
  const [isMSIE, setIsMSIE] = useState(true);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      setGoogleAnalytics(onair.info.channelid);
    }
  }, [onair, onair?.info?.channelid]);

  useEffect(() => {
    if (onair === null || props.apis.info === null) {
      alert('방송 정보를 가져올 수 없습니다.');
      window.location.href = '/live';
      return;
    }

    const ua = window.navigator.userAgent;
    setIsMSIE(ua.indexOf('MSIE') > -1);

    const LOGIN_JWT = cookieCutter.get('LOGIN_JWT');
    setToken(LOGIN_JWT);

    const channelID = router.query.channelID || 'S01';
    const channelType = getLiveChannelType(channelID);
    const apiChannelPath =
      channelType !== '24tv' ? 'channel' : 'virtual/channel';
    setChannelType(channelType);
    const infoApiUrl = `${props.apis.info}/${apiChannelPath}/${channelID}?v_type=2&platform=pcweb&protocol=hls&ssl=N&rscuse=&jwt-token=&sbsmain=`;

    window.addEventListener('load', function () {
      // 시스템 점검으로 아래 기간 자막 서비스 제공이 중단되는 점 양해 부탁드립니다.
      // [2022년 6월6일(월) 07:30 ~ 6월7일(화) 15:00]
      const current = new Date();
      const startSystemMaintenance = new Date(Date.UTC(2022, 5, 5, 22, 30, 0));
      const endSystemMaintenance = new Date(Date.UTC(2022, 5, 7, 6, 0, 0));
      const ccElements = document.querySelectorAll('.vcob_caption_btn');
      [].forEach.call(ccElements, function (element) {
        element.addEventListener(
          'click',
          function (e) {
            if (
              current > startSystemMaintenance &&
              current < endSystemMaintenance
            ) {
              alert(
                `시스템 점검으로 아래 기간 자막 서비스 제공이 중단되는 점 양해 부탁드립니다.\n[2022년 6월6일(월) 07:30 ~ 6월7일(화) 15:00]`
              );
              e.stopImmediatePropagation();
            }
          },
          false
        );
      });
    });

    let isMounted = true;
    async function fetchData(url) {
      const res = await Axios.get(url, { timeout: 3000 });
      const data = res.data;
      if (isMounted) {
        setOnair(data.onair);
        setMode(null);
      }
      setOnair(data.onair);
      setMode(null);
    }
    fetchData(infoApiUrl);

    return () => {
      isMounted = false;
    };
  }, [router.query.channelID, props.apis.info]);

  const onChangeMode = useCallback(
    (change) => {
      if (mode === change) {
        setMode(null);
      } else {
        setMode(change);
      }
    },
    [mode]
  );

  const onChangeFocus = (focus) => {
    setFocusName(focus);
  };

  const onClickLikeButton = async () => {
    if (onair.info.smr_programid.length > 0) {
      try {
        const res = await Axios.get(
          `${props.apis.like}/${onair.info.smr_programid}${
            token ? 'jwt-token=' + token : ''
          }`
        );
        const data = res.data;
        // 프로그램템플릿 api와 다르게 동작함, 204 안줌 무조건 200
        // 상태로 체크
        if (!isClickLike) {
          setLikeCnt(likeCnt + 1);
          setIsClickLike(true);
        } else {
          alert("이미 '좋아요' 하셨습니다.");
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  return (
    <div className="contents" id="container">
      <link
        rel="stylesheet"
        type="text/css"
        href={process.env.URL_LIVE_CSS_PLAYER + '?v=20221122102953'}
        media="all"
      />
      <link
        rel="stylesheet"
        type="text/css"
        href={process.env.URL_TALKSERVICE_CSS}
        media="all"
      />
      <script src={process.env.URL_LIVE_JS_VENDOR} type="text/javascript" />
      <script
        src={
          onair && onair.info.type === 'Radio'
            ? process.env.URL_LIVE_JS_RADIO_PLAYER
            : process.env.URL_LIVE_JS_PLAYER
        }
        type="text/javascript"
      />
      <script
        src={process.env.NEXT_PUBLIC_URL_LIVE_JS_PLAYER_NEW}
        type="text/javascript"
      />
      <script src={process.env.URL_LIVE_JS_PLAYER_UI} type="text/javascript" />
      <script src={process.env.URL_LIVE_JS_IMA3} type="text/javascript" />
      <script src={process.env.URL_LIVE_JS_JQUERY} type="text/javascript" />
      <script src={process.env.URL_LIVE_JS_SOCKET_IO} type="text/javascript" />
      <script
        src={process.env.URL_LIVE_JS_SOCKET_IO_V2_0_3}
        type="text/javascript"
      />
      <script src={process.env.URL_TALKSERVICE_JS} type="text/javascript" />
      <script
        src={process.env.URL_TALKSERVICE_KAKAO_JS}
        type="text/javascript"
      />
      <script src={process.env.URL_LIVE_JS_SENDBIRD} type="text/javascript" />
      <script
        async
        src="https://www.googletagmanager.com/gtag/js?id=UA-146376328-1"
        type="text/javascript"
      />
      <Head>
        <link
          key="css_pc_url"
          rel="stylesheet"
          href={`${process.env.NEXT_PUBLIC_GLOBAL_CSS_PC_URL}?v=${process.env.NEXT_PUBLIC_BUILD_TIME}`}
        />
        <meta
          key="viewport"
          name="viewport"
          content="width=device-width, initial-scale=1"
        />
        <script
          key="gnb_js_url"
          type="text/javascript"
          src={process.env.NEXT_PUBLIC_GNB_JS_URL}
        />

        <meta name="description" content={props.metaTagData.shareDescription} />
        <meta name="keywords" content={props.metaTagData.keywords} />
        <meta name="twitter:card" content="summary" />
        <noscript>Your browser does not support JavaScript!</noscript>
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="ko_KR" />
        <meta property="og:site_name" content="SBS" />
        <meta
          property="og:title"
          content={props.metaTagData.shareTitle || ''}
        />
        <meta
          property="og:description"
          content={props.metaTagData.shareDescription || ''}
        />
        <meta
          property="og:image"
          content={props.metaTagData.shareImageUrl || ''}
        />
        <meta property="og:url" content={props.metaTagData.currentUrl || ''} />
        <meta name="nate:site_name" content="SBS" />
        <meta name="nate:title" content={props.metaTagData.shareTitle || ''} />
        <meta
          name="nate:description"
          content={props.metaTagData.shareDescription || ''}
        />
        <meta
          name="nate:image"
          content={props.metaTagData.shareImageUrl || ''}
        />
        <meta name="nate:url" content={props.metaTagData.currentUrl || ''} />
        <title>{props.metaTagData.shareTitle}</title>
      </Head>

      <div className="live_end_wrap">
        <div
          className={`contents_area_player ${
            mode === MODE.LIVE_MODE_TRANSLATE ||
            (mode === MODE.LIVE_MODE_CHATTING && token !== null)
              ? 'player_side_current'
              : ''
          }`}
        >
          <div className="live_player_w">
            <div className="live_player_inner">
              {!onair && (
                <Image
                  src="/images/player_area.jpeg"
                  alt="방송이 종료 되었습니다"
                  quality="50"
                  width={1200}
                  height={667}
                />
              )}
              {onair && onair.info && <Player data={onair.info} />}
              {onair && onair.info && (
                <Info
                  data={{
                    channelid: onair.info.channelid,
                    channelname:
                      onair.info.channelid === 'S19'
                        ? '고릴라M'
                        : onair.info.channelname,
                    title: onair.info.title,
                    time: onair.info.starttime
                      ? `${onair.info.starttime} ~ ${onair.info.endtime}`
                      : '',
                    guest_name: onair.info.guest_name,
                    homeurl: onair.info.homeurl,
                    programid: onair.info.programid,
                    like_cnt: likeCnt,
                    info: onair.info,
                    chats: props.chats,
                    apis: props.apis,
                  }}
                  onClickLikeButton={onClickLikeButton}
                  onChangeMode={onChangeMode}
                  focusname={focusname}
                  onChangeFocus={onChangeFocus}
                />
              )}
            </div>
          </div>
          {mode && !isMSIE && (
            <Side
              data={props}
              mode={mode}
              channel={onair.info.channelid}
              token={token}
              onBackFocus={onChangeFocus}
              onError={() => {
                setMode(null);
              }}
            />
          )}
        </div>

        <div className="contents_middle_ad">
          <div className="middle_ad_inner">
            <iframe
              title="배너광고"
              src="/html/pc/live_web_middle_97090.htm"
              width="970"
              height="90"
              scrolling="no"
              allowtransparency="ture"
              style={{ border: 0, margin: '0px' }}
            ></iframe>
          </div>
        </div>

        {onair &&
          props.schedules &&
          Array.isArray(props.schedules) &&
          props.schedules.length > 0 && (
            <div className="contents_area_schedule">
              <Title
                title={`${
                  onair.info.channelid === 'S19'
                    ? '고릴라M'
                    : onair.info.channelname
                } 편성표`}
                desc="전체 편성표"
                link={toPlanUrl(onair.info.channelid)}
              />
              <Slider
                type="schedule"
                clickParams="div=live_end"
                data={props.schedules}
              />
            </div>
          )}

        {isVirtualChannel(onair?.info?.channelid) &&
          Array.isArray(props.channels.virtual) &&
          props.channels.virtual.length > 0 && (
            <div className="contents_area_live">
              <Title title="24시간 TV" />
              <Slider
                type="thumb"
                clickParams="div=live_end"
                data={props.channels.virtual}
              />
            </div>
          )}

        {props.channels &&
          props.channels.tv &&
          Array.isArray(props.channels.tv) &&
          props.channels.tv.length > 0 && (
            <div className="contents_area_live">
              <Title title="라이브 TV" />
              <Slider
                type="thumb"
                clickParams="div=live_end"
                data={props.channels.tv}
              />
            </div>
          )}

        {props.channels &&
          props.channels.radio &&
          props.channels.radio.length > 0 &&
          Array.isArray(props.channels.radio) && (
            <div className="contents_area_live">
              <Title title="라이브 라디오" />
              <Slider
                type="thumb"
                clickParams="div=live_end"
                data={props.channels.radio}
              />
            </div>
          )}

        {!isVirtualChannel(onair?.info?.channelid) &&
          Array.isArray(props.channels.virtual) &&
          props.channels.virtual.length > 0 && (
            <div className="contents_area_live">
              <Title title="24시간 TV" />
              <Slider
                type="thumb"
                clickParams="div=live_end"
                data={props.channels.virtual}
              />
            </div>
          )}

        {props.vods &&
          props.vods.list &&
          Array.isArray(props.vods.list) &&
          props.vods.list.length > 0 && (
            <div className="contents_area_vod">
              <Title title={props.vods.title} />
              <Slider
                type="thumb"
                relate_id="vod"
                relate_type={props.vods.type}
                clickParams="div=live_end"
                data={props.vods.list}
              />
            </div>
          )}

        {props.clips &&
          props.clips.list &&
          Array.isArray(props.clips.list) &&
          props.clips.list.length > 0 && (
            <div className="contents_area_clip">
              <Title title={props.clips.title} />
              <Slider
                type="thumb"
                bottom_type="live_recommand_clip"
                clickParams="div=live_end"
                data={props.clips.list}
              />
            </div>
          )}
        <div className="contents_side_ad sideAd_right">
          <div className="side_ad_inner">
            <iframe
              title="배너광고"
              src="/html/pc/live_web_right_120600.htm"
              width="120"
              height="600"
              scrolling="no"
              allowtransparency="ture"
              style={{ border: 0, margin: '0px' }}
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
};

export async function getServerSideProps(ctx) {
  // 가상 또는 라디오채널은 채널이름 명확하지 않아 채널아이디로 하는게 좋아보임
  const channelID = ctx.params.channelID;

  if (!channelID) {
    return {
      redirect: {
        destination: '/live',
        permanent: false,
      },
    };
  }
  const channelType = getLiveChannelType(channelID);

  // header에서 meta teg에 넣어줄 데이터를 가져 오기 위함
  const metaTagData = getMetaTagData(ctx.req.url, channelID);
  const event = await eventData();

  const data = await onairData(channelID, channelType);
  if (data?.onair?.info.title) {
    metaTagData.shareTitle += data.onair.info.title;
    metaTagData.keywords = metaTagData.keywords.replace(
      '무료 스트리밍',
      data.onair.info.title + ' 무료 스트리밍'
    );
    metaTagData.keywords = metaTagData.keywords.replace(
      '무료 정주행',
      data.onair.info.title + ' 무료 정주행'
    );
    metaTagData.shareDescription = metaTagData.shareDescription.replace(
      '무료 스트리밍',
      data.onair.info.title + ' 무료 스트리밍'
    );
  }

  const channelName =
    data === null
      ? '채널정보 없음'
      : channelID === 'S19'
      ? '고릴라M'
      : data && data.onair !== undefined
      ? data.onair.info.channelname
      : '';
  const channels = await channelData(channelID);
  const plan = await getLivePlanArray(channelID);

  let vodType,
    vodTitle,
    clipTitle,
    vodData,
    clipData,
    _vodData,
    _clipData,
    _vodArray,
    _clipArray;
  if (
    data &&
    (data.onair.info.smr_programid ||
      data.onair.info.vod_mediaids ||
      data.onair.info.clip_mediaids)
  ) {
    try {
      const recommandVodApiUrl = `${process.env.NEXT_PUBLIC_API_LIVE_RECOMMAND_VOD}/new/${data.onair.info.smr_programid}`;
      _vodData = await Axios.get(recommandVodApiUrl, { timeout: 3000 });
      _vodArray = _vodData.data;

      if (_vodArray.length > 0) {
        vodType = 'vod';
        vodTitle = `${
          _vodArray[0]?.pgm_nm ? _vodArray[0].pgm_nm + ' ' : ''
        }최신 VOD`;
        vodData = Array.isArray(_vodArray)
          ? _vodArray.map((x) => ({
              relate_type: 'vod',
              srs_div: '01',
              content_date: x.content_date || null,
              content_num: x.content_num || null,
              content: x.content || null,
              title: x.title,
              img: x.pgm_img_url,
              info: { title: x.pgm_nm, desc: x.title },
              link: x.link_url || 'https://www.sbs.co.kr',
            }))
          : null;
      } else {
        vodType = 'curation_vod';
        _vodData = await Axios.get(
          process.env.NEXT_PUBLIC_API_LIVE_RECOMMAND_VOD_ALL,
          { timeout: 3000 }
        );
        _vodArray = _vodData.data;
        vodTitle = '최신 VOD';
        vodData = Array.isArray(_vodArray)
          ? _vodArray.map((x) => ({
              relate_type: 'curation_vod',
              content_date: x.content_date || null,
              content_num: x.content_num || null,
              date: '',
              desc: x.pgm_nm || x.title,
              img: x.pgm_img_url || x.thumb.small,
              imgDesc: x.pgm_nm || null,
              pgm_nm: x.pgm_nm || null,
              srs_div: '01',
              srs_dtl_no: '',
              title: x.pgm_nm || x.title,
              srs_no: x.content_num || null,
              link: x.link_url || 'https://www.sbs.co.kr',
            }))
          : null;
      }
    } catch (e) {
      console.error(e);
    }

    try {
      if (channelType !== '24tv') {
        const recommandClipApiUrl = `${process.env.NEXT_PUBLIC_API_LIVE_RECOMMAND_CLIP}/top/${data.onair.info.smr_programid}`;
        _clipData = await Axios.get(recommandClipApiUrl, { timeout: 3000 });
        _clipArray =
          channelType !== '24tv' ? _clipData.data : _clipData.data.list;
        clipTitle = `${
          _clipArray[0]?.pgm_nm
            ? (_clipArray[0].pgm_nm === 'GorealraM'
                ? '고릴라M'
                : _clipArray[0].pgm_nm) + ' '
            : ''
        }인기 클립`;
      } else {
        _clipData = await Axios.get(
          channelType !== 'radio'
            ? process.env.NEXT_PUBLIC_API_LIVE_RECOMMAND_CLIP_ALL
            : process.env.NEXT_PUBLIC_API_LIVE_RECOMMAND_RADIO_ALL,
          { timeout: 3000 }
        );
        _clipArray = _clipData.data;
        clipTitle = '인기 클립';
        clipData = Array.isArray(_clipArray)
          ? _clipArray.map((x) => ({
              time: toTimeStamp(x.playtime),
              title: x.title,
              desc: x.pgm_nm || channelName,
              img: x.pgm_img_url || x.thumb.small,
              link: x.link_url || 'https://www.sbs.co.kr',
            }))
          : null;
      }

      if (_clipArray.length === 0) {
        _clipData = await Axios.get(
          channelType !== 'radio'
            ? process.env.NEXT_PUBLIC_API_LIVE_RECOMMAND_CLIP_ALL
            : process.env.NEXT_PUBLIC_API_LIVE_RECOMMAND_RADIO_ALL,
          { timeout: 3000 }
        );
        _clipArray = _clipData.data;
        clipTitle = channelName + ' 인기 클립';
      }

      clipData = Array.isArray(_clipArray)
        ? _clipArray.map((x) => ({
            time: toTimeStamp(x.playtime),
            title: x.title,
            desc: x.pgm_nm || channelName,
            img: x.pgm_img_url || x.thumb.small,
            link: x.link_url || 'https://www.sbs.co.kr',
          }))
        : null;
    } catch (e) {
      console.error(e);
    }
  } else {
    try {
      vodType = 'curation_vod';
      _vodData = await Axios.get(
        process.env.NEXT_PUBLIC_API_LIVE_RECOMMAND_VOD_ALL,
        { timeout: 3000 }
      );
      _vodArray = _vodData.data;
      vodTitle = '최신 VOD';
      vodData = Array.isArray(_vodArray)
        ? _vodArray.map((x) => ({
            relate_type: 'curation_vod',
            content_date: x.content_date || null,
            content_num: x.content_num || null,
            date: '',
            desc: x.pgm_nm || x.title,
            img: x.pgm_img_url || x.thumb.small,
            imgDesc: x.pgm_nm || null,
            pgm_nm: x.pgm_nm || null,
            srs_div: '01',
            srs_dtl_no: '',
            title: x.pgm_nm || x.title,
            srs_no: x.content_num || null,
            link: x.link_url || 'https://www.sbs.co.kr',
          }))
        : null;

      _clipData = await Axios.get(
        channelType !== 'radio'
          ? process.env.NEXT_PUBLIC_API_LIVE_RECOMMAND_CLIP_ALL
          : process.env.NEXT_PUBLIC_API_LIVE_RECOMMAND_RADIO_ALL,
        { timeout: 3000 }
      );
      _clipArray = _clipData.data;
      clipTitle = '인기 클립';
      clipData = Array.isArray(_clipArray)
        ? _clipArray.map((x) => ({
            time: toTimeStamp(x.playtime),
            title: x.title,
            desc: x.pgm_nm || channelName,
            img: x.pgm_img_url || x.thumb.small,
            link: x.link_url || 'https://www.sbs.co.kr',
          }))
        : null;
    } catch (e) {
      console.error(e);
    }
  }

  let programLikeCnt;
  if (data && data.onair.info.programid) {
    try {
      const likeCnt = await Axios.get(
        process.env.NEXT_PUBLIC_API_LIVE_PROGRAM_LIKE_COUNT +
          '/' +
          data.onair.info.smr_programid,
        { timeout: 3000 }
      );
      programLikeCnt = likeCnt.data?.count;
    } catch (e) {
      console.error(e);
    }
  }

  const apis = {
    info: process.env.NEXT_PUBLIC_API_LIVE_INFO,
    chatting_channel: process.env.NEXT_PUBLIC_API_LIVE_CHATTING_CHANNELS,
    chatting_user: process.env.NEXT_PUBLIC_API_LIVE_CHATTING_USER,
    chatting_report: process.env.NEXT_PUBLIC_API_LIVE_CHATTING_REPORT,
    translate: process.env.NEXT_PUBLIC_URL_LIVE_TRANSLATE_SOCKET,
    like: process.env.NEXT_PUBLIC_API_LIVE_PROGRAM_LIKE_SEND,
  };

  return {
    props: {
      data: data || null,
      event: event || null,
      schedules: plan || null,
      channels: channels || null,
      vods:
        vodType && vodTitle && vodData
          ? { type: vodType, title: vodTitle, list: vodData }
          : null,
      clips:
        clipTitle && clipData ? { title: clipTitle, list: clipData } : null,
      like_cnt: programLikeCnt || null,
      apis: apis || null,
      metaTagData,
    },
  };
}

const onairData = async (channelID, channelType) => {
  try {
    const apiChannelPath =
      channelType !== '24tv' ? 'channel' : 'virtual/channel';
    const infoApiUrl = `${process.env.NEXT_PUBLIC_API_LIVE_INFO}/${apiChannelPath}/${channelID}?v_type=2&platform=pcweb&protocol=hls&ssl=N&rscuse=&jwt-token=&sbsmain=`;

    const res = await Axios.get(infoApiUrl, { timeout: 3000 });
    const data = res.data;

    return data;
  } catch (e) {
    console.error(e);
  }
};

const channelData = async (channelID) => {
  try {
    const tvChannelList = await Axios.get(
      process.env.NEXT_PUBLIC_API_LIVE_CHANNELS,
      { timeout: 3000 }
    );
    let channelTVData = tvChannelList.data.list.filter(
      (item) => item.type === 'TV' && item.channelid !== channelID
    );
    channelTVData = channelTVData.map((x) => ({
      title: x.title,
      desc: x.channelname,
      // link: `/live/${x.channelid}`,
      channelid: x.channelid,
      link:
        x.channelid === 'EVENT4' && x.homeurl.length > 0
          ? x.homeurl
          : `/live/${x.channelid}` || '',
      pgm_title: x.title,
      img:
        x.channelid !== 'EVENT4'
          ? x.thumbs?.['640'] || x.thumbimg || x.program_image
          : x.thumbimg,
      starttime: x.starttime || null,
      endtime: x.endtime || null,
    }));

    const radioChannelList = await Axios.get(
      process.env.NEXT_PUBLIC_API_LIVE_CHANNELS,
      { timeout: 3000 }
    );
    const dupRadioArray = radioChannelList.data.list.filter(
      (item) => item.type === 'Radio' && item.onair_yn === 'Y'
    );
    let channelRadioData = deduplArray(
      dupRadioArray,
      'channelname',
      'channelid'
    );
    channelRadioData = channelRadioData.map((x) => ({
      title: x.title,
      desc: x.channelid === 'S19' ? '고릴라M' : x.channelname,
      link: `/live/${x.channelid}`,
      channelid: x.channelid,
      pgm_title: x.title,
      img: x.thumbs?.['640'] || x.thumbimg || x.program_image,
      starttime: x.starttime || null,
      endtime: x.endtime || null,
    }));

    const virtualChannelList = await Axios.get(
      `${process.env.NEXT_PUBLIC_API_LIVE_CHANNELS_24TV}?v_type=2`,
      { timeout: 3000 }
    );
    let channel24Data = virtualChannelList.data.list.filter(
      (item) => item.channelid !== channelID
    );
    channel24Data = channel24Data.map((x) => ({
      title: x.title,
      desc: x.channelname,
      link: `/live/${x.channelid}`,
      pgm_tms_title: x.title,
      img: x.thumbs?.['640'] || x.thumbimg || x.program_image,
      starttime: x.starttime || null,
      endtime: x.endtime || null,
    }));

    return {
      tv: channelTVData,
      radio: channelRadioData,
      virtual: channel24Data,
    };
  } catch (e) {
    console.error(e);
  }
};

const eventData = async () => {
  try {
    const liveEventList = await Axios.get(
      process.env.NEXT_PUBLIC_API_LIVE_FORMATION_EVENT,
      { timeout: 3000 }
    );
    const eventData = liveEventList.data;
    return eventData;
  } catch (e) {
    console.error(e);
  }
};

const setGoogleAnalytics = (channelID) => {
  let tagId, ga4TagId;
  switch (channelID) {
    case 'S02': // sbs sports
      tagId = 'UA-146376328-1';
      ga4TagId = 'G-8G453WZ76V';
      break;
    case 'S03': // sbs plus
      tagId = 'UA-146760740-2';
      ga4TagId = 'G-97W8RH442C';
      break;
    case 'S04': // sbs fune
      tagId = 'UA-146760740-3';
      ga4TagId = 'G-WQWE5WDT9F';
      break;
    // case 'S05': // sbs golf
    //   tagId = 'UA-100348188-1';
    //   break;
    case 'S06': // sbs biz
      tagId = 'UA-135761934-1';
      break;
    case 'S09': // sbs m
      tagId = 'UA-146888827-4';
      ga4TagId = 'G-0DM6QEB0KJ';
      break;
    case 'S10': // kizmom, S20 kpop,
      tagId = 'UA-146888827-5';
      ga4TagId = 'G-YHQ768DD4V';
      break;
    case 'S11': // fil
      ga4TagId = 'G-VL8DPHLZZJ';
      break;
    default:
      tagId = null;
      ga4TagId = null;
      break;
  }

  if (ga4TagId || tagId) {
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', ga4TagId ? ga4TagId : tagId, {
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_title: window.document.title,
    });
  }
};

export default LiveViewPage;
